import { useState, useCallback, createContext } from "react";
import MediaQuery from "../utilities/MediaQuery";
import {
  accessTokenValue,
  getStoredUserId,
  isAuthenticatedUser,
  isLoggedInUserInfo,
  markSignInForRenewal,
  readStoredToken,
} from "../utilities/AuthState";
import { trackEvent } from "../utilities/analytics";

export const UserContext = createContext();

const safeParseStorageJSON = (key, fallbackValue) => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) {
      return fallbackValue;
    }

    const parsed = JSON.parse(raw);
    return parsed ?? fallbackValue;
  } catch (error) {
    return fallbackValue;
  }
};

const loggedOutUser = () => ({
  userId: "",
  userType: "",
  vendor: "",
  userName: "",
  email: "",
  isLoggedIn: false,
});

const readRestoredUser = () => {
  const user = safeParseStorageJSON("user", {});
  const token = readStoredToken();
  if (!isAuthenticatedUser(user, token)) {
    if (getStoredUserId(user) || accessTokenValue(token)) {
      markSignInForRenewal();
      localStorage.removeItem("token");
      localStorage.setItem("user", JSON.stringify(loggedOutUser()));
    }
    return loggedOutUser();
  }

  return {
    userId: getStoredUserId(user),
    userType: user.userType || "",
    vendor: user.vendor || "",
    userName: user.userName || "",
    email: user.email || "",
    isLoggedIn: true,
  };
};

const UserContextProvider = (props) => {
  const localCollection = safeParseStorageJSON("collectionId", []);
  const [userInformation, setUserInformation] = useState(readRestoredUser);

  const [collection, setCollection] = useState(
    Array.isArray(localCollection) ? localCollection : [],
  );

  const handleLogOutUser = useCallback(() => {
    const logoutInfo = loggedOutUser();
    setUserInformation(logoutInfo);
    localStorage.setItem("user", JSON.stringify(logoutInfo));
    localStorage.removeItem("token");
  }, []);

  const media = MediaQuery();

  const value = {
    media,
    userInformation,
    changeUserInformation: (value) => {
      const nextValue = {
        userId: value?.userId || "",
        userType: value?.userType || "",
        vendor: value?.vendor || "",
        userName: value?.userName || "",
        email: value?.email || "",
        isLoggedIn: isLoggedInUserInfo(value),
      };

      setUserInformation(nextValue);
      localStorage.setItem("user", JSON.stringify(nextValue));
    },
    changelogOutUser: handleLogOutUser,
    collection,
    changeCollection: (value) => {
      if (collection.includes(value)) {
        setCollection(collection.filter((like) => like !== value));
        localStorage.setItem(
          "collectionId",
          JSON.stringify(collection.filter((like) => like !== value)),
        );
        trackEvent("collection_remove", { material_id: value });
      } else {
        setCollection([...collection, value]);
        localStorage.setItem(
          "collectionId",
          JSON.stringify([...collection, value]),
        );
        trackEvent("collection_add", { material_id: value });
      }
    },
    updateCollection: (value) => {
      const nextCollection = Array.isArray(value) ? value : [];
      setCollection(nextCollection);
      localStorage.setItem("collectionId", JSON.stringify(nextCollection));
    },
  };

  return (
    <UserContext.Provider value={value}>{props.children}</UserContext.Provider>
  );
};

export default UserContextProvider;
